/**
 * Format: Tagalys
 * -----------------------------------------------------------------------------
 * Formats Tagalys API product responses.
 * - https://tagalys.notion.site/Recommendations-product-based-merchandisable-0d037e5dc9cb42918e29b6a9073fbe23
 *
 */
import { formatColorOptions, formatOption } from '~/format/format-option'
import { formatPriceAmount } from '~/format/format-price'
import { buildSwatchUrl } from '~/format/format-variant'

import { getDisabledColorCodes, isColorCodeInStr } from '~/helpers/product'

/**
 * Format product.
 * @param {Object} product - JSON response.
 * @returns {Object}
 */
export function formatProduct(product) {
  const formattedProduct = {
    available: product.available,
    compareAtPrice: {
      amount: formatPriceAmount(product.compare_at_price),
      currencyCode: cnvs.store.currency,
      max: formatPriceAmount(product.compare_at_price_max),
      min: formatPriceAmount(product.compare_at_price_min),
      varies: product.compare_at_price_varies,
    },
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: product.id,
    images: formatImages(product),
    price: {
      amount: formatPriceAmount(product.price),
      currencyCode: cnvs.store.currency,
      max: formatPriceAmount(product.price_max),
      min: formatPriceAmount(product.price_min),
      varies: product.price_varies,
    },
    tags: product.tags,
    title: product.title,
    type: product.type,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    variants: formatVariants(product),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0] || {}
  formattedProduct.secondaryImage = formattedProduct.images[1] || {}
  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)
  formattedProduct.colorOptions = formatColorOptions(formattedProduct, formattedProduct.variants)

  return formattedProduct
}

/**
 * Format image array from product response.
 * @param {Object} product - Product object.
 * @returns {Array}
 */
function formatImages(product) {
  if (product.images.length <= 0) {
    return []
  }

  return product.images.filter((image) => {
    return !getDisabledColorCodes(product.tags)
      .some((colorCode) => isColorCodeInStr(colorCode, image.src))
  })
}

/**
 * Format product options array.
 * @param {Object} product - Product response.
 * @param {Array} variants - Formatted variants response.
 * @returns {Array}
 */
function formatOptions(product, variants) {
  return product.options_with_values.map((option, index) => {
    return formatOption(option, { index, product, variants })
  })
}

/**
 * Format variant array.
 * @param {Object} product - Product object.
 * @returns {Array}
 */
function formatVariants(product) {
  return product.variants.map((variant) => formatVariant(variant, product.options))
}

/**
 * Format product variant.
 * @param {Object} variant - Variant response.
 * @param {Array} options - Product option names array.
 * @returns {Object}
 */
function formatVariant(variant, options) {
  const optionKeys = Object.keys(variant).filter((variantKey) => {
    return variantKey.includes('option')
  })

  const selectedVariantOptions = optionKeys.map((key, index) => {
    return {
      name: options[index],
      value: variant[key],
    }
  })

  const formattedVariant = {
    available: variant.available,
    compareAtPrice: {
      amount: formatPriceAmount(variant.compare_at_price),
      currencyCode: cnvs.store.currency,
    },
    id: variant.id,
    image: variant.image?.id ?? false,
    imageSrc: variant.image?.src ?? false,
    inventory: variant.quantityAvailable,
    price: {
      amount: formatPriceAmount(variant.price),
      currencyCode: cnvs.store.currency,
    },
    selectedOptions: selectedVariantOptions,
    title: variant.title,
    sku: variant.sku,
  }

  formattedVariant.swatchUrl = buildSwatchUrl(formattedVariant)

  return formattedVariant
}
